"use client";

import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

type CartTotals = {
  totalMrp?: number;
  totalDiscount?: number;
  totalAmount?: number;
};

export interface OrderBreakdownProps {
  cart: CartTotals | null | undefined;
  currencyCode: string;
  /** desktop = full card rows; compact = dense for sticky bar */
  variant?: "desktop" | "compact";
}

const rowSx = (compact: boolean) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  py: compact ? 0.35 : 0.75,
  px: compact ? 0 : 0.25,
});

export function OrderBreakdown({
  cart,
  currencyCode,
  variant = "desktop",
}: OrderBreakdownProps) {
  const compact = variant === "compact";
  const subtotal = cart?.totalMrp ?? 0;
  const discount = cart?.totalDiscount ?? 0;
  const total = cart?.totalAmount ?? 0;

  return (
    <Box component="div">
      <Box sx={rowSx(compact)}>
        <Typography
          variant={compact ? "caption" : "body2"}
          sx={{
            color: "text.secondary",
            fontWeight: compact ? 500 : 600,
            fontSize: compact ? "0.72rem" : undefined,
          }}
        >
          Subtotal
        </Typography>
        <Typography
          variant={compact ? "caption" : "body2"}
          sx={{
            fontWeight: 600,
            fontSize: compact ? "0.75rem" : undefined,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {currencyCode}
          {subtotal}
        </Typography>
      </Box>
      <Box sx={rowSx(compact)}>
        <Typography
          variant={compact ? "caption" : "body2"}
          sx={{
            color: "#1f7a4d",
            fontWeight: compact ? 500 : 600,
            fontSize: compact ? "0.72rem" : undefined,
          }}
        >
          Discount
        </Typography>
        <Typography
          variant={compact ? "caption" : "body2"}
          sx={{
            fontWeight: 700,
            color: "#1f7a4d",
            fontSize: compact ? "0.75rem" : undefined,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          −{currencyCode}
          {discount}
        </Typography>
      </Box>
      <Box sx={rowSx(compact)}>
        <Typography
          variant={compact ? "caption" : "body2"}
          sx={{
            color: "text.secondary",
            fontWeight: compact ? 500 : 600,
            fontSize: compact ? "0.72rem" : undefined,
          }}
        >
          Delivery
        </Typography>
        <Typography
          variant={compact ? "caption" : "body2"}
          sx={{
            fontWeight: 600,
            color: "text.secondary",
            fontSize: compact ? "0.75rem" : undefined,
          }}
        >
          Free
        </Typography>
      </Box>
      <Box
        sx={{
          mt: compact ? 0.5 : 1.25,
          pt: compact ? 0.75 : 1.25,
          borderTop: `1px solid ${alpha("#1f7a4d", 0.12)}`,
          ...rowSx(compact),
        }}
      >
        <Typography
          variant={compact ? "body2" : "subtitle1"}
          sx={{
            fontWeight: 800,
            letterSpacing: compact ? "-0.02em" : "-0.03em",
            fontSize: compact ? "0.85rem" : undefined,
          }}
        >
          Total
        </Typography>
        <Typography
          variant={compact ? "body2" : "subtitle1"}
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            fontSize: compact ? "0.95rem" : undefined,
            color: "#0b1710",
          }}
        >
          {currencyCode}
          {total}
        </Typography>
      </Box>
    </Box>
  );
}
